package com.sddamico.swigmore.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.components.sections.HeaderAccount
import com.sddamico.swigmore.ds.core.PageTitle
import com.sddamico.swigmore.models.LeagueResponse
import com.sddamico.swigmore.models.MeResponse
import com.sddamico.swigmore.util.ApiResource
import com.sddamico.swigmore.util.AppTab
import com.sddamico.swigmore.util.AuthState
import com.sddamico.swigmore.util.CachedResource
import com.sddamico.swigmore.util.LoadState
import com.sddamico.swigmore.util.Routes
import com.sddamico.swigmore.util.rememberAuthState
import com.sddamico.swigmore.util.rememberCachedResource
import com.sddamico.swigmore.util.rememberNow
import com.sddamico.swigmore.util.timeAgo
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import kotlinx.browser.window
import kotlinx.coroutines.launch

/**
 * What a signed-in screen gets: who is signed in, and the league data the header's sync chip loads,
 * so a page that needs it doesn't fetch it again.
 */
class SignedIn(val me: MeResponse, val league: ApiResource<LeagueResponse>)

/**
 * [PageLayout] for the screens behind sign-in: sends a signed-out visitor to `/signin?next=` and back,
 * and fills the header with the account menu and the Fantrax sync chip. [adminOnly] screens show a
 * notice to owners instead of their content.
 */
@Composable
fun SignedInPage(
  title: String,
  activeTab: AppTab?,
  adminOnly: Boolean = false,
  content: @Composable (SignedIn) -> Unit,
) {
  val ctx = rememberPageContext()
  val auth = rememberAuthState()
  val session = auth.value

  LaunchedEffect(session) {
    // The export has no session; redirecting there would bake the sign-in screen into this page's
    // static HTML, which every visitor then sees until the app loads.
    if (session is LoadState.Ready && session.value == null && !AppGlobals.isExporting) {
      // Replacing this page in history, so Back from sign-in doesn't land on it and bounce again.
      ctx.router.navigateTo(Routes.signIn(window.location.pathname + window.location.search), UpdateHistoryMode.REPLACE)
    }
  }

  val me = (session as? LoadState.Ready)?.value
  when {
    session is LoadState.Failed -> PageLayout(title, activeTab = activeTab) {
      ContentColumn { PageTitle("Swigmore coaches poll", "Can't load your account.", lede = session.message) }
    }
    me == null -> PageLayout(title, activeTab = activeTab) {}
    else -> SignedInLayout(title, activeTab, me, auth, adminOnly, content)
  }
}

@Composable
private fun SignedInLayout(
  title: String,
  activeTab: AppTab?,
  me: MeResponse,
  auth: AuthState,
  adminOnly: Boolean,
  content: @Composable (SignedIn) -> Unit,
) {
  val scope = rememberCoroutineScope()
  var signingOut by remember { mutableStateOf(false) }
  val league = rememberCachedResource(CachedResource.League)
  val signedIn = remember(me, league) { SignedIn(me, league) }
  val now = rememberNow(60_000)
  PageLayout(
    title,
    activeTab = activeTab,
    syncStatus = league.value?.sync?.lastSuccessAt?.let { "Fantrax synced ${timeAgo(it, now)}" },
    account = HeaderAccount(me.email, me.name, signingOut) {
      if (!signingOut) {
        signingOut = true
        scope.launch { auth.logout() }
      }
    },
    showAdmin = me.isAdmin,
  ) {
    if (adminOnly && !me.isAdmin) {
      ContentColumn { PageTitle("Commish", "Commissioners only.", lede = "This page is for the league commissioners.") }
    } else {
      content(signedIn)
    }
  }
}
